/**
 * 요약 4칸 (2026-08-21) — 안읽음 · 중요 · 첨부 · 나에게(TO)를 한 줄에 컬럼 넷으로.
 *
 * 색 규칙 두 층:
 *  ① 무엇을 뜻하나 — 아이콘 색은 **목록 안에서 그 표시가 쓰는 색 그대로**(별=주황, 첨부=중립).
 *     새 색을 만들지 않는다.
 *  ② 주의가 필요하나 — 넷 중 손이 가야 하는 것은 안읽음뿐이라 **숫자까지** 주의색으로 올린다.
 *     0 이 되면 중립으로 내려간다.
 *
 * ★ 첨부·나에게(TO) 는 **서버가 목록에 그 정보를 안 실어 준다**(MailListItem 에 칸이 없다).
 *   자리는 잡아 두고 숫자는 `—`, 누를 수 없게 둔다 — 있는 척하면 눌렀을 때 거짓말이 된다.
 * ★ 안읽음·중요는 **지금 불러온 목록** 기준이다. 더 받을 게 남아 있으면 `+` 를 붙인다
 *   ("적어도 이만큼" 은 참이다).
 */

import { Paperclip, Star, AtSign } from '../../icons';
import { tMail } from '../../lib/mail-i18n';

export type MailStatKey = 'unread' | 'star' | 'clip' | 'to';

export interface MailStatCounts {
  unread: number;
  star: number;
  /** 서버가 안 주면 null — 화면은 `—` 로 그린다. */
  clip: number | null;
  to: number | null;
}

interface MailStatRowProps {
  counts: MailStatCounts;
  /** 켜져 있는 조건들. */
  active: Set<MailStatKey>;
  /** 더 받을 게 남아 있나 — 숫자에 `+` 를 붙일지. */
  partial: boolean;
  onToggle: (key: MailStatKey) => void;
}

const CELLS: { key: MailStatKey; labelKey: 'mail.stat.unread' | 'mail.stat.star' | 'mail.stat.clip' | 'mail.stat.to' }[] = [
  { key: 'unread', labelKey: 'mail.stat.unread' },
  { key: 'star', labelKey: 'mail.stat.star' },
  { key: 'clip', labelKey: 'mail.stat.clip' },
  { key: 'to', labelKey: 'mail.stat.to' },
];

function iconOf(key: MailStatKey) {
  if (key === 'star') return <Star size={11} aria-hidden />;
  if (key === 'clip') return <Paperclip size={11} aria-hidden />;
  if (key === 'to') return <AtSign size={11} aria-hidden />;
  return <span className="mail-stat-dot" aria-hidden />;
}

export function MailStatRow({ counts, active, partial, onToggle }: MailStatRowProps) {
  return (
    <div className="mail-stat-row" role="group" aria-label={tMail('mail.stat.aria')}>
      {CELLS.map(({ key, labelKey }) => {
        const value = counts[key];
        const na = value === null;
        const on = active.has(key);
        const zero = !na && value === 0;
        return (
          <button
            key={key}
            type="button"
            className={`mail-stat mail-stat--${key}${zero ? ' mail-stat--zero' : ''}${na ? ' mail-stat--na' : ''}`}
            aria-pressed={na ? undefined : on}
            disabled={na}
            title={na ? tMail('mail.stat.na_title') : (partial ? tMail('mail.stat.partial_title') : undefined)}
            onClick={() => { if (!na) onToggle(key); }}
          >
            <span className="mail-stat-k">{iconOf(key)}{tMail(labelKey)}</span>
            <span className="mail-stat-v">
              {na ? '—' : `${value}${partial && value > 0 ? '+' : ''}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
